import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  fetchBaseballFixtureContext,
  fetchBaseballFixtureH2H,
  fetchBaseballFixtureSummary,
  fetchBaseballPlayerOverview,
  fetchBaseballPlayerProfile,
  fetchBaseballTeamRoster,
  fetchFixtureJson,
  fetchPlayerOdds,
} from "../api";

const C = {
  page: "#07110d",
  panel: "linear-gradient(180deg, rgba(12,24,19,0.96), rgba(7,15,12,0.98))",
  card: "rgba(255,255,255,0.03)",
  border: "rgba(148,163,184,0.18)",
  text: "#e7f5ec",
  sub: "#95ab9d",
  green: "#31d48b",
  greenDeep: "#16b96b",
  amber: "#fbbf24",
};

const PANEL = {
  border: `1px solid ${C.border}`,
  borderRadius: 18,
  background: C.panel,
  boxShadow: "0 12px 26px rgba(0,0,0,0.22)",
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const toArray = (value) => (Array.isArray(value) ? value : []);

const PROP_GROUPS = [
  ["hits", "Hits"],
  ["home_runs", "Home Runs"],
  ["rbi", "RBI"],
  ["runs", "Runs"],
  ["total_bases", "Total Bases"],
  ["singles", "Singles"],
  ["doubles", "Doubles"],
  ["triples", "Triples"],
  ["stolen_bases", "Stolen Bases"],
  ["pitcher_strikeouts", "Pitcher Strikeouts"],
  ["pitcher_outs", "Pitcher Outs"],
  ["pitcher_walks_issued", "Pitcher Walks Issued"],
  ["pitcher_hits_allowed", "Pitcher Hits Allowed"],
];

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Europe/London",
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const isPitcherMarket = (groupKey) => groupKey.startsWith("pitcher_");

const normalizeMarketKey = (value = "") => {
  let key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^player_/, "")
    .replace(/\s+/g, "_");
  key = key.replace(/__(?=.)/g, "_");
  if (key.endsWith("_over")) return { groupKey: key.slice(0, -5), side: "over" };
  if (key.endsWith("_under")) return { groupKey: key.slice(0, -6), side: "under" };
  return { groupKey: key, side: "" };
};

const formatBaseballPropMarket = (marketKey = "") =>
  firstDefined(
    PROP_GROUPS.find(([key]) => key === normalizeMarketKey(marketKey).groupKey)?.[1],
    String(marketKey || "")
      .replace(/^player_/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase()),
    "Prop"
  );

const formatBaseballPropSide = (marketKey = "", line = null) => {
  const { side } = normalizeMarketKey(marketKey);
  const sideLabel = side ? `${side.charAt(0).toUpperCase()}${side.slice(1)}` : "Line";
  return line != null ? `${sideLabel} ${line}` : sideLabel;
};

const formatEdgePercent = (edge) => {
  const number = Number(edge);
  if (!Number.isFinite(number)) return "—";
  const percent = number * 100;
  return `${percent > 0 ? "+" : ""}${percent.toFixed(1)}%`;
};

const formatModelPercent = (probability) => {
  const number = Number(probability);
  if (!Number.isFinite(number)) return "—";
  return `${(number * 100).toFixed(1)}%`;
};

const edgeTone = (edge) => {
  const number = Number(edge);
  if (!Number.isFinite(number)) return C.sub;
  if (number > 0.01) return "#86efac";
  if (number < -0.01) return "#fca5a5";
  return C.sub;
};

const normalizePropSideData = (row, parsed) => ({
  marketKey: firstDefined(row.market, row.market_type, row.market_key, ""),
  side: parsed.side,
  marketLabel: firstDefined(row.market_label, formatBaseballPropMarket(firstDefined(row.market, row.market_type, row.market_key, ""))),
  sideLabel: firstDefined(row.side_label, formatBaseballPropSide(firstDefined(row.market, row.market_type, row.market_key, ""), firstDefined(row.line, row.handicap, row.total, null))),
  price: normalizePrice(firstDefined(row.price, row.odds, null)),
  modelProb: normalizePrice(firstDefined(row.model_prob, row.model_probability, null)),
  fairPrice: normalizePrice(firstDefined(row.fair_price, row.fair_odds, null)),
  edge: normalizePrice(firstDefined(row.edge, row.edge_pct, row.edge_percent, null)),
  expectedMean: normalizePrice(firstDefined(row.expected_mean, row.mean, null)),
  plainSummary: firstDefined(row.plain_summary, row.summary, null),
  why: firstDefined(row.why, row.explanation, null),
  keyFactors: toArray(firstDefined(row.key_factors, row.factors, [])),
  modelSource: firstDefined(row.model_source, row.source, null),
});

const playerHeadshot = (playerId) =>
  playerId ? `https://img.mlbstatic.com/mlb-photos/image/upload/w_180,q_auto:best/v1/people/${playerId}/headshot/67/current` : "";

const teamLogo = (team) =>
  firstDefined(
    team?.logo,
    team?.logo_url,
    team?.crest,
    team?.badge,
    team?.id ? `https://www.mlbstatic.com/team-logos/${team.id}.svg` : ""
  );

const formatRecord = (snapshot) => {
  if (!snapshot || snapshot.played == null) return "Season record pending";
  return `${snapshot.wins ?? 0}-${snapshot.losses ?? 0}`;
};

const formatSnapshotValue = (value, digits = 1) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(digits) : "—";
};

const formatRunDiff = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number > 0 ? "+" : ""}${number}`;
};

function TeamBadge({ team, name, size = 64 }) {
  const [failed, setFailed] = useState(false);
  const logo = teamLogo(team);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size,
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        flexShrink: 0,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(148,163,184,0.18)",
        color: C.text,
        fontSize: Math.max(18, Math.round(size * 0.36)),
        fontWeight: 900,
      }}
    >
      {logo && !failed ? (
        <img
          src={logo}
          alt={`${name} logo`}
          width={size}
          height={size}
          style={{ width: "78%", height: "78%", objectFit: "contain" }}
          onError={() => setFailed(true)}
        />
      ) : (
        String(name || "?").charAt(0).toUpperCase()
      )}
    </div>
  );
}

function FormStrip({ form }) {
  // MLB schedules arrive newest-first; display chronologically so the latest
  // result is the badge furthest to the right.
  const rows = [...toArray(form).slice(0, 10)].reverse();
  if (!rows.length) return <span style={{ color: C.sub, fontSize: 12 }}>Form pending</span>;
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {rows.map((result, index) => {
        const won = String(result).toUpperCase() === "W";
        return (
          <span
            key={`${result}-${index}`}
            style={{
              width: 23,
              height: 23,
              borderRadius: 7,
              display: "grid",
              placeItems: "center",
              background: won ? "rgba(49,212,139,0.18)" : "rgba(248,113,113,0.14)",
              border: `1px solid ${won ? "rgba(49,212,139,0.42)" : "rgba(248,113,113,0.34)"}`,
              color: won ? "#86efac" : "#fca5a5",
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            {won ? "W" : "L"}
          </span>
        );
      })}
    </div>
  );
}

function RecentGames({ games, teamName }) {
  const rows = toArray(games).slice(0, 5);
  if (!rows.length) {
    return <div style={{ color: C.sub, fontSize: 12 }}>Recent scores are being prepared.</div>;
  }

  return (
    <div style={{ display: "grid", gap: 7 }}>
      {rows.map((game, index) => {
        const won = game.result === "W";
        const date = game.game_date
          ? new Date(game.game_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
          : "—";
        return (
          <div
            key={game.game_pk || `${game.opponent}-${index}`}
            style={{
              display: "grid",
              gridTemplateColumns: "52px minmax(0, 1fr) auto auto",
              gap: 9,
              alignItems: "center",
              padding: "8px 10px",
              borderRadius: 10,
              background: C.card,
              border: `1px solid ${C.border}`,
              fontSize: 12,
            }}
          >
            <span style={{ color: C.sub }}>{date}</span>
            <span style={{ color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {game.is_home ? "vs" : "@"} {game.opponent || "Opponent"}
            </span>
            <strong style={{ color: C.text }}>{game.score || `${game.runs_for ?? "—"}-${game.runs_against ?? "—"}`}</strong>
            <span style={{ color: won ? "#86efac" : "#fca5a5", fontWeight: 900 }}>{game.result || "—"}</span>
          </div>
        );
      })}
      <span style={{ color: C.sub, fontSize: 11 }}>{teamName} score shown first.</span>
    </div>
  );
}

function LinePerformance({ teamName, performance, side }) {
  const season = performance?.season;
  const recent = performance?.last_five;
  if (!season?.played && !recent?.played) return null;
  const sideLabel = side === "under" ? "Under" : "Over";
  const renderRecord = (label, record) => {
    if (!record?.played) return null;
    const percentage = side === "under" ? record.under_pct : record.over_pct;
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "72px minmax(0, 1fr) auto",
          gap: 12,
          alignItems: "center",
          padding: "11px 0",
          borderTop: `1px solid ${C.border}`,
        }}
      >
        <strong style={{ color: C.sub, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</strong>
        <div>
          <div style={{ color: C.text, fontWeight: 800 }}>
            {record.over} over · {record.under} under
            {record.push ? ` · ${record.push} push${record.push === 1 ? "" : "es"}` : ""}
          </div>
          <div style={{ marginTop: 3, color: C.sub, fontSize: 12 }}>
            Average combined runs {record.average_total ?? "—"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: C.green, fontSize: 18, fontWeight: 900 }}>
            {percentage != null ? `${(percentage * 100).toFixed(1)}%` : "—"}
          </div>
          <div style={{ color: C.sub, fontSize: 11 }}>{sideLabel} rate</div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "13px 15px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
        <div style={{ color: C.text, fontSize: 16, fontWeight: 900 }}>{teamName}</div>
        <div style={{ color: C.green, fontSize: 12, fontWeight: 800 }}>{sideLabel} trend</div>
      </div>
      {renderRecord("Season", season)}
      {renderRecord("Last 5", recent)}
    </div>
  );
}

function ComparisonBar({
  label,
  awayValue,
  homeValue,
  lowerIsBetter = false,
  formatter = formatSnapshotValue,
  awayDisplay,
  homeDisplay,
}) {
  const away = Number(awayValue);
  const home = Number(homeValue);
  const validAway = awayValue !== null && awayValue !== undefined && Number.isFinite(away);
  const validHome = homeValue !== null && homeValue !== undefined && Number.isFinite(home);
  const safeAway = validAway ? Math.max(0, away) : 0;
  const safeHome = validHome ? Math.max(0, home) : 0;
  const total = safeAway + safeHome || 1;
  const awayLeads = validAway && validHome && (lowerIsBetter ? away < home : away > home);
  const homeLeads = validAway && validHome && (lowerIsBetter ? home < away : home > away);

  return (
    <div style={{ display: "grid", gap: 7 }}>
      <div style={{ display: "grid", gridTemplateColumns: "72px 1fr 72px", gap: 10, alignItems: "center" }}>
        <strong style={{ color: awayLeads ? C.green : C.text, textAlign: "right" }}>
          {awayDisplay ?? formatter(awayValue)}
        </strong>
        <span style={{ color: C.sub, textAlign: "center", fontSize: 12 }}>{label}</span>
        <strong style={{ color: homeLeads ? C.green : C.text }}>
          {homeDisplay ?? formatter(homeValue)}
        </strong>
      </div>
      <div style={{ display: "flex", gap: 4, height: 6 }}>
        <div style={{ flex: safeAway / total, minWidth: 3, borderRadius: 999, background: awayLeads ? C.green : "rgba(148,163,184,0.34)" }} />
        <div style={{ flex: safeHome / total, minWidth: 3, borderRadius: 999, background: homeLeads ? C.green : "rgba(148,163,184,0.34)" }} />
      </div>
    </div>
  );
}

const normalizePrice = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeAnalysis = (payload) => {
  const analysis = payload?.analysis || payload;
  if (!analysis?.why) return null;
  return {
    why: analysis.why,
    edge: normalizePrice(analysis.edge),
    modelProb: normalizePrice(firstDefined(analysis.model_prob, analysis.modelProb, null)),
    fairPrice: normalizePrice(firstDefined(analysis.fair_price, analysis.fairPrice, null)),
    price: normalizePrice(firstDefined(analysis.price, payload?.price, null)),
    bookmaker: firstDefined(analysis.bookmaker, payload?.bookmaker, payload?.book, null),
  };
};

const normalizeMarketSummary = (summary) => {
  const marketSummary = summary || {};
  const props = marketSummary?.props || {};
  const propRows = toArray(firstDefined(props.rows, props.items, []));

  return {
    moneyline: {
      away: marketSummary?.moneyline?.away
        ? {
            price: normalizePrice(marketSummary.moneyline.away.price),
            bookmaker: firstDefined(marketSummary.moneyline.away.bookmaker, marketSummary.moneyline.away.book),
            analysis: normalizeAnalysis(marketSummary.moneyline.away),
          }
        : null,
      home: marketSummary?.moneyline?.home
        ? {
            price: normalizePrice(marketSummary.moneyline.home.price),
            bookmaker: firstDefined(marketSummary.moneyline.home.bookmaker, marketSummary.moneyline.home.book),
            analysis: normalizeAnalysis(marketSummary.moneyline.home),
          }
        : null,
    },
    runLine: {
      away: marketSummary?.run_line?.away
        ? {
            line: firstDefined(marketSummary.run_line.away.line, marketSummary.run_line.away.handicap),
            price: normalizePrice(marketSummary.run_line.away.price),
            bookmaker: firstDefined(marketSummary.run_line.away.bookmaker, marketSummary.run_line.away.book),
          }
        : null,
      home: marketSummary?.run_line?.home
        ? {
            line: firstDefined(marketSummary.run_line.home.line, marketSummary.run_line.home.handicap),
            price: normalizePrice(marketSummary.run_line.home.price),
            bookmaker: firstDefined(marketSummary.run_line.home.bookmaker, marketSummary.run_line.home.book),
          }
        : null,
    },
    total: {
      over: marketSummary?.total?.over
        ? {
            line: firstDefined(marketSummary.total.over.line, marketSummary.total.over.total),
            price: normalizePrice(marketSummary.total.over.price),
            bookmaker: firstDefined(marketSummary.total.over.bookmaker, marketSummary.total.over.book),
            why: firstDefined(marketSummary.total.over.why, marketSummary.total.over.analysis?.why, null),
            edge: normalizePrice(firstDefined(marketSummary.total.over.edge, marketSummary.total.over.analysis?.edge, null)),
            modelProb: normalizePrice(firstDefined(marketSummary.total.over.model_prob, marketSummary.total.over.analysis?.model_prob, null)),
            fairPrice: normalizePrice(firstDefined(marketSummary.total.over.fair_price, marketSummary.total.over.analysis?.fair_price, null)),
            performance: firstDefined(marketSummary.total.over.performance, marketSummary.total.over.analysis?.performance, null),
          }
        : null,
      under: marketSummary?.total?.under
        ? {
            line: firstDefined(marketSummary.total.under.line, marketSummary.total.under.total),
            price: normalizePrice(marketSummary.total.under.price),
            bookmaker: firstDefined(marketSummary.total.under.bookmaker, marketSummary.total.under.book),
            why: firstDefined(marketSummary.total.under.why, marketSummary.total.under.analysis?.why, null),
            edge: normalizePrice(firstDefined(marketSummary.total.under.edge, marketSummary.total.under.analysis?.edge, null)),
            modelProb: normalizePrice(firstDefined(marketSummary.total.under.model_prob, marketSummary.total.under.analysis?.model_prob, null)),
            fairPrice: normalizePrice(firstDefined(marketSummary.total.under.fair_price, marketSummary.total.under.analysis?.fair_price, null)),
            performance: firstDefined(marketSummary.total.under.performance, marketSummary.total.under.analysis?.performance, null),
          }
        : null,
    },
    recommendedTotal: marketSummary?.recommended_total
      ? {
          side: String(marketSummary.recommended_total.side || "").toLowerCase(),
          line: firstDefined(marketSummary.recommended_total.line, null),
          price: normalizePrice(marketSummary.recommended_total.price),
          bookmaker: firstDefined(marketSummary.recommended_total.bookmaker, marketSummary.recommended_total.book),
          why: firstDefined(marketSummary.recommended_total.why, null),
          edge: normalizePrice(firstDefined(marketSummary.recommended_total.edge, null)),
          modelProb: normalizePrice(firstDefined(marketSummary.recommended_total.model_prob, null)),
          fairPrice: normalizePrice(firstDefined(marketSummary.recommended_total.fair_price, null)),
          performance: firstDefined(marketSummary.recommended_total.performance, null),
        }
      : null,
    firstInning: {
      yes: marketSummary?.first_inning?.yes
        ? {
            price: normalizePrice(marketSummary.first_inning.yes.price),
            bookmaker: firstDefined(marketSummary.first_inning.yes.bookmaker, marketSummary.first_inning.yes.book),
            analysis: normalizeAnalysis(marketSummary.first_inning.yes),
          }
        : null,
      no: marketSummary?.first_inning?.no
        ? {
            price: normalizePrice(marketSummary.first_inning.no.price),
            bookmaker: firstDefined(marketSummary.first_inning.no.bookmaker, marketSummary.first_inning.no.book),
            analysis: normalizeAnalysis(marketSummary.first_inning.no),
          }
        : null,
    },
    props: {
      rows: propRows,
      players: firstDefined(props.players, props.player_count, null),
      markets: firstDefined(props.markets, props.market_count, null),
      rowCount: propRows.length,
    },
  };
};

const getStatBucket = (payload, preferred = "") => {
  const stats = payload?.stats || {};
  const current = stats?.current || {};
  const previous = stats?.previous || {};
  if (preferred && (current?.[preferred] || previous?.[preferred])) {
    return {
      current: current?.[preferred] || {},
      previous: previous?.[preferred] || {},
      previousSeason: stats?.previous_season,
      season: stats?.season,
    };
  }

  const currentFirst = current?.pitching || current?.hitting || current?.batting || {};
  const previousFirst = previous?.pitching || previous?.hitting || previous?.batting || {};
  return {
    current: currentFirst,
    previous: previousFirst,
    previousSeason: stats?.previous_season,
    season: stats?.season,
  };
};

const summarizePitcher = (payload) => {
  const bucket = getStatBucket(payload, "pitching");
  const line = bucket.current || {};
  return {
    era: firstDefined(line.era, line.earnedRunAverage, line.eraDisplay),
    whip: firstDefined(line.whip),
    innings: firstDefined(line.innings_pitched, line.inningsPitched, line.ip),
    strikeouts: firstDefined(line.strikeOuts, line.strike_outs, line.strikeouts, line.so),
    wins: firstDefined(line.wins),
    losses: firstDefined(line.losses),
  };
};

const summarizeBatter = (payload) => {
  const bucket = getStatBucket(payload, "hitting");
  const line = bucket.current || {};
  return {
    avg: firstDefined(line.avg, line.batting_average),
    obp: firstDefined(line.obp),
    slg: firstDefined(line.slg),
    ops: firstDefined(line.ops),
    hits: firstDefined(line.hits),
    homeRuns: firstDefined(line.home_runs, line.homeRuns),
    rbi: firstDefined(line.rbi),
  };
};

const supportedPropGroups = new Set(PROP_GROUPS.map(([key]) => key));

const normalizePlayerOddsRows = (payload) => {
  const rawRows = toArray(
    firstDefined(
      payload?.rows,
      payload?.data?.rows,
      payload?.player_odds,
      Array.isArray(payload) ? payload : []
    )
  );

  return rawRows.filter((row) => {
    const parsed = normalizeMarketKey(firstDefined(row.market, row.market_type, row.market_key, ""));
    return supportedPropGroups.has(parsed.groupKey);
  });
};

const mergePropRows = (rows) => {
  const merged = {};
  rows.forEach((row, index) => {
    const playerName = String(firstDefined(row.player_name, row.player, row.name, "Unknown Player")).trim();
    const playerId = firstDefined(row.player_id, row.provider_player_id, row.participant_id, null);
    const parsed = normalizeMarketKey(firstDefined(row.market, row.market_type, row.market_key, ""));
    const line = firstDefined(row.line, row.handicap, row.total, null);
    const bookmaker = firstDefined(row.bookmaker, row.book, "—");
    const key = `${parsed.groupKey}|${playerId || playerName}|${line ?? ""}|${bookmaker}`;
    if (!merged[key]) {
      merged[key] = {
        id: key || `row-${index}`,
        groupKey: parsed.groupKey,
        playerId,
        playerName,
        teamId: firstDefined(row.team_id, row.provider_team_id, null),
        teamName: firstDefined(row.team_name, row.team, null),
        marketType: firstDefined(row.market_type, row.market_key, row.market, ""),
        line,
        bookmaker,
        marketLabel: firstDefined(row.market_label, formatBaseballPropMarket(parsed.groupKey)),
        overPrice: null,
        underPrice: null,
        overData: null,
        underData: null,
        bestEdge: null,
        bestSide: "",
        bestData: null,
        rawRows: [],
      };
    }
    merged[key].rawRows.push(row);
    const sideData = normalizePropSideData(row, parsed);
    if (parsed.side === "over") {
      merged[key].overPrice = sideData.price != null ? sideData.price : merged[key].overPrice;
      merged[key].overData = sideData;
    } else if (parsed.side === "under") {
      merged[key].underPrice = sideData.price != null ? sideData.price : merged[key].underPrice;
      merged[key].underData = sideData;
    } else if (merged[key].overPrice == null) {
      merged[key].overPrice = sideData.price != null ? sideData.price : merged[key].overPrice;
      merged[key].overData = sideData;
    }

    [merged[key].overData, merged[key].underData].forEach((candidate) => {
      if (candidate?.edge == null) return;
      if (merged[key].bestEdge == null || candidate.edge > merged[key].bestEdge) {
        merged[key].bestEdge = candidate.edge;
        merged[key].bestSide = candidate.side || "over";
        merged[key].bestData = candidate;
      }
    });
  });

  const grouped = Object.values(merged).reduce((acc, item) => {
    if (!item.groupKey) return acc;
    if (!acc[item.groupKey]) acc[item.groupKey] = [];
    acc[item.groupKey].push(item);
    return acc;
  }, {});

  Object.values(grouped).forEach((items) =>
    items.sort((a, b) => a.playerName.localeCompare(b.playerName))
  );

  return grouped;
};

const playerPositionLabel = (player, overview = null) =>
  firstDefined(
    overview?.position?.abbreviation,
    overview?.position?.code,
    overview?.position?.name,
    player?.position?.abbreviation,
    player?.position?.code,
    player?.position?.name,
    null
  );

const playerBatLabel = (player, overview = null) =>
  firstDefined(
    overview?.bat_side?.description,
    overview?.bat_side?.code,
    player?.bat_side?.description,
    player?.bat_side?.code,
    null
  );

const playerThrowLabel = (player, overview = null) =>
  firstDefined(
    overview?.pitch_hand?.description,
    overview?.pitch_hand?.code,
    player?.pitch_hand?.description,
    player?.pitch_hand?.code,
    null
  );

function Chip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "7px 12px",
        borderRadius: 999,
        border: active ? "1px solid rgba(49,212,139,0.75)" : `1px solid ${C.border}`,
        background: active ? "rgba(22,185,107,0.18)" : "rgba(255,255,255,0.05)",
        color: C.text,
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {children}
    </button>
  );
}

function SectionTitle({ title, sub }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: C.text, fontSize: 20, fontWeight: 800 }}>{title}</div>
      {sub ? <div style={{ marginTop: 4, color: C.sub, fontSize: 13 }}>{sub}</div> : null}
    </div>
  );
}

function PlayerIdentityButton({ player, subtitle, onClick, headshotUrl }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        gap: 10,
        alignItems: "center",
        background: "transparent",
        border: 0,
        padding: 0,
        color: "inherit",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 999,
          overflow: "hidden",
          background: "rgba(255,255,255,0.08)",
          flexShrink: 0,
        }}
      >
        {headshotUrl ? (
          <img
            src={headshotUrl}
            alt=""
            width={40}
            height={40}
            style={{ objectFit: "cover" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : null}
      </div>
      <div>
        <div style={{ color: C.text, fontWeight: 800 }}>{player?.full_name || player?.player_name || "Player"}</div>
        {subtitle ? <div style={{ marginTop: 2, color: C.sub, fontSize: 12 }}>{subtitle}</div> : null}
      </div>
    </button>
  );
}

function StatComparisonBlock({ label, current, previous }) {
  return (
    <div style={{ background: C.card, borderRadius: 12, padding: 10, border: `1px solid ${C.border}` }}>
      <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 4, color: C.text, fontWeight: 800 }}>{current ?? "—"}</div>
      <div style={{ marginTop: 2, color: C.sub, fontSize: 12 }}>Prev: {previous ?? "—"}</div>
    </div>
  );
}

function FactorChips({ factors }) {
  const items = toArray(factors).filter(Boolean).slice(0, 4);
  if (!items.length) return null;
  return (
    <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
      {items.map((factor) => (
        <span
          key={factor}
          style={{
            padding: "4px 8px",
            borderRadius: 999,
            border: `1px solid ${C.border}`,
            background: "rgba(255,255,255,0.04)",
            color: "#d7e7dd",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {factor}
        </span>
      ))}
    </div>
  );
}

function PitcherCard({ label, pitcher, overview, onOpen }) {
  const playerStats = Object.keys(overview?.stats?.current?.pitching || {}).length ? overview : pitcher;
  const summary = summarizePitcher(playerStats);
  const bucket = getStatBucket(playerStats, "pitching");
  return (
    <div style={{ ...PANEL, padding: 14 }}>
      <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.7 }}>{label}</div>
      {pitcher ? (
        <>
          <div style={{ marginTop: 8 }}>
            <PlayerIdentityButton
              player={pitcher}
              onClick={onOpen}
              headshotUrl={firstDefined(overview?.headshot_url, pitcher?.headshot_url, playerHeadshot(pitcher?.id))}
              subtitle={`${pitcher.position?.abbreviation || "P"} • ${pitcher.pitch_hand?.description || "Handedness TBD"}`}
            />
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", color: "#d7e7dd", fontSize: 12 }}>
            <span>{pitcher.height || "—"}</span>
            <span>{pitcher.weight ? `${pitcher.weight} lbs` : "—"}</span>
            <span>Season {bucket.season || "—"}</span>
            <span>Prev {bucket.previousSeason || "—"}</span>
          </div>
          <div style={{ marginTop: 12, display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))" }}>
            <StatComparisonBlock label="ERA" current={summary.era} previous={bucket.previous?.era} />
            <StatComparisonBlock label="WHIP" current={summary.whip} previous={bucket.previous?.whip} />
            <StatComparisonBlock label="IP" current={summary.innings} previous={firstDefined(bucket.previous?.innings_pitched, bucket.previous?.inningsPitched, bucket.previous?.ip)} />
            <StatComparisonBlock label="SO" current={summary.strikeouts} previous={firstDefined(bucket.previous?.strike_outs, bucket.previous?.strikeOuts, bucket.previous?.strikeouts)} />
            <StatComparisonBlock label="W-L" current={summary.wins != null || summary.losses != null ? `${summary.wins ?? "—"}-${summary.losses ?? "—"}` : "—"} previous={bucket.previous?.wins != null || bucket.previous?.losses != null ? `${bucket.previous?.wins ?? "—"}-${bucket.previous?.losses ?? "—"}` : "—"} />
          </div>
        </>
      ) : (
        <div style={{ marginTop: 8, color: C.sub }}>Probable pitcher not confirmed.</div>
      )}
    </div>
  );
}

export default function BaseballGameDetail() {
  const { id } = useParams();
  const location = useLocation();
  const [fixtureJson, setFixtureJson] = useState(null);
  const [context, setContext] = useState(null);
  const [h2hData, setH2hData] = useState(null);
  const [propsRows, setPropsRows] = useState([]);
  const [propsMeta, setPropsMeta] = useState(null);
  const [overviews, setOverviews] = useState({});
  const [fallbackRosters, setFallbackRosters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [propSearch, setPropSearch] = useState("");
  const [activePropFilter, setActivePropFilter] = useState("all");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedPlayerLoading, setSelectedPlayerLoading] = useState(false);
  const [selectedModelRead, setSelectedModelRead] = useState("");

  const requestedTab = new URLSearchParams(location.search).get("tab");
  const [tab, setTab] = useState(requestedTab === "props" ? "props" : "overview");

  useEffect(() => {
    if (requestedTab === "props") setTab("props");
  }, [requestedTab]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const load = async () => {
      const [fixtureResult, summaryResult] = await Promise.allSettled([
        fetchFixtureJson(id),
        fetchBaseballFixtureSummary(id),
      ]);
      if (cancelled) return;

      const fixtureData = fixtureResult.status === "fulfilled" ? fixtureResult.value : null;
      const summaryData = summaryResult.status === "fulfilled" ? summaryResult.value : null;
      if (!fixtureData && !summaryData) {
        setError("Could not load baseball matchup.");
        setLoading(false);
        return;
      }

      setFixtureJson(
        fixtureData || {
          fixture: {
            id,
            away_team: summaryData?.teams?.away?.name || "Away",
            home_team: summaryData?.teams?.home?.name || "Home",
            kickoff_utc: summaryData?.game_date,
            comp: "MLB",
            status: summaryData?.status?.detailed || summaryData?.status?.abstract,
          },
        }
      );
      setContext(summaryData);
      setLoading(false);

      const [contextResult, h2hResult, propsResult] = await Promise.allSettled([
        fetchBaseballFixtureContext(id),
        fetchBaseballFixtureH2H({ fixtureId: id, seasonsBack: 1 }),
        fetchPlayerOdds({ fixtureId: id }),
      ]);
      if (cancelled) return;

      const contextData = contextResult.status === "fulfilled" ? contextResult.value : null;
      const h2hPayload = h2hResult.status === "fulfilled" ? h2hResult.value : null;
      const propsData = propsResult.status === "fulfilled" ? propsResult.value : { rows: [] };
      if (contextData) {
        setContext((previous) => ({
          ...(previous || {}),
          ...contextData,
          score: previous?.score || contextData?.score,
          teams: {
            home: { ...(contextData?.teams?.home || {}), ...(previous?.teams?.home || {}) },
            away: { ...(contextData?.teams?.away || {}), ...(previous?.teams?.away || {}) },
          },
          probable_pitchers: contextData?.probable_pitchers || previous?.probable_pitchers,
          market_summary: contextData?.market_summary || previous?.market_summary,
        }));
      }
      setH2hData(h2hPayload);
      setPropsRows(normalizePlayerOddsRows(propsData));
      setPropsMeta(propsData);

      const enrichedContext = contextData || summaryData;
      const probablePitchers = [
        { pitcher: enrichedContext?.probable_pitchers?.home, teamId: enrichedContext?.teams?.home?.id },
        { pitcher: enrichedContext?.probable_pitchers?.away, teamId: enrichedContext?.teams?.away?.id },
      ].filter((row) => row.pitcher?.id);
      Promise.allSettled(
        probablePitchers.map((row) =>
          fetchBaseballPlayerProfile({
            playerId: row.pitcher.id,
            season: 2026,
          })
        )
      ).then((results) => {
        if (cancelled) return;
        const nextOverviews = {};
        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            nextOverviews[String(probablePitchers[index].pitcher.id)] = result.value;
          }
        });
        setOverviews((previous) => ({ ...previous, ...nextOverviews }));
      });

      const teams = enrichedContext?.teams || {};
      Promise.allSettled(
        ["home", "away"]
          .map((side) => teams?.[side])
          .filter((team) => team?.id)
          .map((team) => fetchBaseballTeamRoster({ teamId: team.id, season: 2026 }))
      ).then((results) => {
        if (cancelled) return;
        const rosterMap = {};
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            rosterMap[String(result.value?.team_id)] = toArray(result.value?.roster);
          }
        });
        setFallbackRosters(rosterMap);
      });
    };

    load().catch(() => {
      if (!cancelled) {
        setError("Could not load baseball matchup.");
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const fixture = fixtureJson?.fixture;
  const marketSummary = useMemo(() => normalizeMarketSummary(context?.market_summary), [context]);
  const groupedProps = useMemo(() => mergePropRows(propsRows), [propsRows]);
  const propsMetaSummary = useMemo(
    () => ({
      players: firstDefined(propsMeta?.players, propsMeta?.player_count, marketSummary.props.players, null),
      markets: firstDefined(propsMeta?.markets, propsMeta?.market_count, marketSummary.props.markets, null),
      bookmakers: toArray(firstDefined(propsMeta?.bookmakers, propsMeta?.books, [])),
      count: firstDefined(propsMeta?.count, propsRows.length, null),
    }),
    [marketSummary.props.markets, marketSummary.props.players, propsMeta, propsRows.length]
  );

  const homePitcher = firstDefined(context?.probable_pitchers?.home, null);
  const awayPitcher = firstDefined(context?.probable_pitchers?.away, null);
  const homeOverview = homePitcher ? overviews[String(homePitcher.id)] : null;
  const awayOverview = awayPitcher ? overviews[String(awayPitcher.id)] : null;
  const homeTeam = context?.teams?.home || {};
  const awayTeam = context?.teams?.away || {};
  const homeSnapshot = homeTeam?.snapshot || null;
  const awaySnapshot = awayTeam?.snapshot || null;
  const gameScore = context?.score || {};
  const awayScore = firstDefined(gameScore?.away, fixture?.away_score, fixture?.score?.away, "—");
  const homeScore = firstDefined(gameScore?.home, fixture?.home_score, fixture?.score?.home, "—");
  const inningLabel = [
    firstDefined(gameScore?.inning_state, context?.status?.detailed, null),
    gameScore?.current_inning ? `Inning ${gameScore.current_inning}` : null,
  ].filter(Boolean).join(" • ");

  const homeRoster = toArray(firstDefined(context?.teams?.home?.roster, fallbackRosters[String(context?.teams?.home?.id)], []));
  const awayRoster = toArray(firstDefined(context?.teams?.away?.roster, fallbackRosters[String(context?.teams?.away?.id)], []));

  useEffect(() => {
    if (tab !== "rosters" || !fixture) return undefined;

    let cancelled = false;
    const rosterTargets = [
      { teamId: context?.teams?.home?.id, roster: homeRoster },
      { teamId: context?.teams?.away?.id, roster: awayRoster },
    ]
      .flatMap(({ teamId, roster }) =>
        roster
          .filter((player) => player?.id != null && !overviews[String(player.id)])
          .map((player) => ({ playerId: String(player.id), teamId }))
      );

    if (!rosterTargets.length) return undefined;

    Promise.allSettled(
      rosterTargets.map(({ playerId }) =>
        fetchBaseballPlayerProfile({ playerId, season: 2026 })
      )
    ).then((results) => {
      if (cancelled) return;
      const nextOverviews = {};
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          nextOverviews[rosterTargets[index].playerId] = result.value;
        }
      });
      if (Object.keys(nextOverviews).length) {
        setOverviews((prev) => ({ ...prev, ...nextOverviews }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [awayRoster, context, fixture, homeRoster, overviews, tab]);

  const visiblePropGroups = useMemo(() => {
    const search = propSearch.trim().toLowerCase();
    const entries = Object.entries(groupedProps).filter(([groupKey]) =>
      activePropFilter === "all" ? true : groupKey === activePropFilter
    );
    return entries
      .map(([groupKey, rows]) => [
        groupKey,
        rows.filter((row) =>
          search
            ? row.playerName.toLowerCase().includes(search) ||
              String(row.marketType || "").toLowerCase().includes(search)
            : true
        ),
      ])
      .filter(([, rows]) => rows.length > 0);
  }, [activePropFilter, groupedProps, propSearch]);

  const topPitcherProps = useMemo(() => {
    return Object.entries(groupedProps)
      .filter(([groupKey]) => isPitcherMarket(groupKey))
      .flatMap(([, rows]) => rows)
      .sort((a, b) => (b.bestEdge ?? -Infinity) - (a.bestEdge ?? -Infinity))
      .slice(0, 5);
  }, [groupedProps]);

  const topBatterProps = useMemo(() => {
    return Object.entries(groupedProps)
      .filter(([groupKey]) => !isPitcherMarket(groupKey))
      .flatMap(([, rows]) => rows)
      .sort((a, b) => (b.bestEdge ?? -Infinity) - (a.bestEdge ?? -Infinity))
      .slice(0, 5);
  }, [groupedProps]);

  const allMergedProps = useMemo(
    () => Object.values(groupedProps).flatMap((rows) => rows),
    [groupedProps]
  );

  const rosterPlayerIndex = useMemo(() => {
    const index = {};
    [...homeRoster, ...awayRoster].forEach((player) => {
      if (player?.id != null) index[String(player.id)] = player;
    });
    return index;
  }, [awayRoster, homeRoster]);

  const openPlayerDrawer = async (row) => {
    const playerId = firstDefined(row?.playerId, row?.id) ? String(firstDefined(row?.playerId, row?.id)) : "";
    const rosterPlayer = playerId ? rosterPlayerIndex[playerId] : null;
    const existingOverview = playerId ? overviews[playerId] : null;
    const teamId = firstDefined(
      row?.teamId,
      row?.current_team?.id,
      rosterPlayer
        ? (homeRoster.some((player) => String(player.id) === playerId) ? context?.teams?.home?.id : context?.teams?.away?.id)
        : null
    );
    const teamName = firstDefined(
      row?.teamName,
      row?.current_team?.name,
      rosterPlayer
        ? (homeRoster.some((player) => String(player.id) === playerId) ? fixture.home_team : fixture.away_team)
        : null
    );
    const fixtureRows = allMergedProps.filter(
      (item) =>
        String(item.playerId || "") === playerId ||
        (!playerId && item.playerName === firstDefined(row.playerName, row.full_name))
    );

    setSelectedPlayer({
      row,
      rosterPlayer,
      overview: existingOverview || null,
      teamId,
      teamName,
      fixtureRows,
    });

    // A lightweight profile is preloaded so the drawer opens instantly. Only
    // skip the richer request when recent game data has already been fetched.
    if (!playerId || Array.isArray(existingOverview?.recent_games)) return;

    setSelectedPlayerLoading(true);
    try {
      const overview = await fetchBaseballPlayerOverview({ playerId, season: 2026, teamId });
      setOverviews((prev) => ({ ...prev, [playerId]: overview }));
      setSelectedPlayer((prev) =>
        prev && String(firstDefined(prev.row?.playerId, prev.row?.id, "")) === playerId
          ? { ...prev, overview }
          : prev
      );
    } catch {
      setSelectedPlayer((prev) =>
        prev && String(firstDefined(prev.row?.playerId, prev.row?.id, "")) === playerId
          ? { ...prev, overview: null }
          : prev
      );
    } finally {
      setSelectedPlayerLoading(false);
    }
  };

  const closePlayerDrawer = () => {
    setSelectedPlayer(null);
    setSelectedPlayerLoading(false);
  };

  if (loading) {
    return <div style={{ minHeight: "100vh", background: C.page, color: C.sub, padding: 28 }}>Loading baseball matchup…</div>;
  }

  if (error || !fixture) {
    return <div style={{ minHeight: "100vh", background: C.page, color: "#fca5a5", padding: 28 }}>{error || "No baseball fixture found."}</div>;
  }

  const selectedOverview = selectedPlayer?.overview || null;
  const selectedRosterPlayer = selectedPlayer?.rosterPlayer || null;
  const selectedPitching = firstDefined(
    selectedOverview?.pitch_hand?.description,
    selectedRosterPlayer?.pitch_hand?.description,
    selectedOverview?.pitch_hand?.code,
    selectedRosterPlayer?.pitch_hand?.code,
    "—"
  );
  const selectedBatting = firstDefined(
    selectedOverview?.bat_side?.description,
    selectedRosterPlayer?.bat_side?.description,
    selectedOverview?.bat_side?.code,
    selectedRosterPlayer?.bat_side?.code,
    "—"
  );
  const selectedPosition = firstDefined(
    selectedOverview?.position?.abbreviation,
    selectedRosterPlayer?.position?.abbreviation,
    selectedOverview?.position?.name,
    selectedRosterPlayer?.position?.name,
    "Position TBD"
  );
  const selectedTeam = firstDefined(
    selectedOverview?.current_team?.name,
    selectedPlayer?.teamName,
    homeRoster.some((player) => String(player.id) === String(firstDefined(selectedPlayer?.row?.playerId, selectedPlayer?.row?.id, ""))) ? fixture.home_team : null,
    awayRoster.some((player) => String(player.id) === String(firstDefined(selectedPlayer?.row?.playerId, selectedPlayer?.row?.id, ""))) ? fixture.away_team : null,
    "Team TBD"
  );
  const selectedPitchingStats = summarizePitcher(selectedOverview);
  const selectedBattingStats = summarizeBatter(selectedOverview);
  const selectedBucket = getStatBucket(
    selectedOverview,
    selectedPosition === "P" || String(selectedOverview?.position?.type || "").toLowerCase().includes("pitcher")
      ? "pitching"
      : "hitting"
  );
  const selectedRecentGames = toArray(selectedOverview?.recent_games).slice(0, 6);
  const totalRead = marketSummary.recommendedTotal?.why
    ? {
        label: `${marketSummary.recommendedTotal.side === "under" ? "Under" : "Over"} ${marketSummary.recommendedTotal.line ?? ""}`,
        ...marketSummary.recommendedTotal,
      }
    : marketSummary.total.under?.why
    ? { label: `Under ${marketSummary.total.under.line ?? ""}`, ...marketSummary.total.under }
    : marketSummary.total.over?.why
      ? { label: `Over ${marketSummary.total.over.line ?? ""}`, ...marketSummary.total.over }
      : null;
  const modelReads = [
    totalRead ? { key: `total-${totalRead.label}`, category: "Game total", ...totalRead } : null,
    marketSummary.moneyline.away?.analysis
      ? { key: "away-moneyline", category: "Moneyline", label: `${fixture.away_team} to win`, ...marketSummary.moneyline.away.analysis }
      : null,
    marketSummary.moneyline.home?.analysis
      ? { key: "home-moneyline", category: "Moneyline", label: `${fixture.home_team} to win`, ...marketSummary.moneyline.home.analysis }
      : null,
    marketSummary.firstInning.no?.analysis
      ? { key: "nrfi", category: "First inning", label: "NRFI — No run in the 1st", ...marketSummary.firstInning.no.analysis }
      : null,
    marketSummary.firstInning.yes?.analysis
      ? { key: "yrfi", category: "First inning", label: "YRFI — Run in the 1st", ...marketSummary.firstInning.yes.analysis }
      : null,
  ].filter(Boolean).sort((a, b) => Number(b.edge || 0) - Number(a.edge || 0));
  const activeModelRead = modelReads.find((read) => read.key === selectedModelRead) || modelReads[0] || null;

  return (
    <div style={{ minHeight: "100vh", background: C.page, color: C.text }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "24px 18px 48px" }}>
        <Link to="/baseball" style={{ color: C.green, textDecoration: "none", fontWeight: 700 }}>
          ← Back to MLB board
        </Link>

        <section style={{ ...PANEL, padding: 20, marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: C.green, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 800 }}>
                Matchup Dashboard
              </div>
              <h1 style={{ margin: "8px 0 0", fontSize: 32 }}>
                {fixture.away_team} at {fixture.home_team}
              </h1>
              <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap", color: C.sub, fontSize: 13 }}>
                <span>{fmtTime(firstDefined(context?.game_date, fixture.kickoff_utc))}</span>
                <span>{fixture.comp || "MLB"}</span>
                <span>{firstDefined(context?.venue?.name, fixture.venue, "Venue TBD")}</span>
                <span>{firstDefined(context?.status?.detailed, fixture.status, "Scheduled")}</span>
              </div>
            </div>

            <div style={{ display: "grid", gap: 10, minWidth: 290 }}>
              <div style={{ ...PANEL, padding: 14, background: "rgba(22,185,107,0.10)" }}>
                <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.7 }}>Probable Pitcher Duel</div>
                <div style={{ marginTop: 8, color: C.text, fontSize: 16, fontWeight: 800 }}>
                  {awayPitcher?.full_name || "Away TBD"} vs {homePitcher?.full_name || "Home TBD"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Pitcher K Props", "Power Bats", "Hits Props", "Speed Threats"].map((chip) => (
                  <span
                    key={chip}
                    style={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 999,
                      padding: "4px 10px",
                      background: "rgba(255,255,255,0.04)",
                      color: "#d7e7dd",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 20,
              padding: "22px clamp(14px, 4vw, 34px)",
              borderRadius: 18,
              border: "1px solid rgba(49,212,139,0.22)",
              background:
                "radial-gradient(circle at 50% 0%, rgba(49,212,139,0.13), transparent 55%), rgba(2,10,7,0.72)",
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
              gap: 18,
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
              <TeamBadge team={awayTeam} name={fixture.away_team} />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.text, fontSize: "clamp(16px, 2vw, 23px)", fontWeight: 900 }}>
                  {fixture.away_team}
                </div>
                <div style={{ marginTop: 5, color: C.sub, fontSize: 13 }}>{formatRecord(awaySnapshot)}</div>
              </div>
            </div>

            <div style={{ textAlign: "center", minWidth: 116 }}>
              <div style={{ color: C.sub, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>
                {inningLabel || "Matchup"}
              </div>
              <div style={{ marginTop: 5, color: C.text, fontSize: "clamp(36px, 5vw, 58px)", lineHeight: 1, fontWeight: 900 }}>
                {awayScore} <span style={{ color: C.sub, fontSize: "0.55em" }}>–</span> {homeScore}
              </div>
              <div style={{ marginTop: 7, color: C.green, fontSize: 12, fontWeight: 800 }}>
                {firstDefined(context?.status?.abstract, context?.status?.detailed, fixture.status, "Scheduled")}
              </div>
            </div>

            <div style={{ display: "flex", gap: 14, alignItems: "center", justifyContent: "flex-end", minWidth: 0, textAlign: "right" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.text, fontSize: "clamp(16px, 2vw, 23px)", fontWeight: 900 }}>
                  {fixture.home_team}
                </div>
                <div style={{ marginTop: 5, color: C.sub, fontSize: 13 }}>{formatRecord(homeSnapshot)}</div>
              </div>
              <TeamBadge team={homeTeam} name={fixture.home_team} />
            </div>
          </div>

          <div style={{ marginTop: 18, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Chip active={tab === "overview"} onClick={() => setTab("overview")}>Overview</Chip>
            <Chip active={tab === "props"} onClick={() => setTab("props")}>Player Props</Chip>
            <Chip active={tab === "pitchers"} onClick={() => setTab("pitchers")}>Pitchers</Chip>
            <Chip active={tab === "rosters"} onClick={() => setTab("rosters")}>Rosters</Chip>
          </div>
        </section>

        {tab === "overview" && (
          <div style={{ marginTop: 18, display: "grid", gap: 18 }}>
            <section style={{ ...PANEL, padding: 18 }}>
              <SectionTitle
                title="Season Pulse"
                sub="Current regular-season form, scoring output and run prevention from MLB game data."
              />
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(260px, 1.35fr) minmax(0, 1fr)", gap: 20, alignItems: "center" }}>
                <div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <TeamBadge team={awayTeam} name={fixture.away_team} size={46} />
                    <div>
                      <div style={{ color: C.text, fontWeight: 800 }}>{fixture.away_team}</div>
                      <div style={{ marginTop: 3, color: C.sub, fontSize: 12 }}>
                        {formatRecord(awaySnapshot)} • {awaySnapshot?.win_pct != null ? `${formatSnapshotValue(awaySnapshot.win_pct * 100, 1)}% wins` : "Win rate pending"}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 13 }}><FormStrip form={awaySnapshot?.form} /></div>
                </div>

                <div style={{ display: "grid", gap: 13 }}>
                  <ComparisonBar
                    label="Win rate"
                    awayValue={awaySnapshot?.win_pct != null ? awaySnapshot.win_pct * 100 : null}
                    homeValue={homeSnapshot?.win_pct != null ? homeSnapshot.win_pct * 100 : null}
                    formatter={(value) => value == null ? "—" : `${formatSnapshotValue(value, 1)}%`}
                  />
                  <ComparisonBar label="Runs / game" awayValue={awaySnapshot?.runs_per_game} homeValue={homeSnapshot?.runs_per_game} />
                  <ComparisonBar label="Runs allowed" awayValue={awaySnapshot?.runs_allowed_per_game} homeValue={homeSnapshot?.runs_allowed_per_game} lowerIsBetter />
                  <ComparisonBar
                    label="Run diff"
                    awayValue={100 + Number(awaySnapshot?.run_diff || 0)}
                    homeValue={100 + Number(homeSnapshot?.run_diff || 0)}
                    awayDisplay={formatRunDiff(awaySnapshot?.run_diff)}
                    homeDisplay={formatRunDiff(homeSnapshot?.run_diff)}
                  />
                </div>

                <div style={{ textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
                    <div>
                      <div style={{ color: C.text, fontWeight: 800 }}>{fixture.home_team}</div>
                      <div style={{ marginTop: 3, color: C.sub, fontSize: 12 }}>
                        {formatRecord(homeSnapshot)} • {homeSnapshot?.win_pct != null ? `${formatSnapshotValue(homeSnapshot.win_pct * 100, 1)}% wins` : "Win rate pending"}
                      </div>
                    </div>
                    <TeamBadge team={homeTeam} name={fixture.home_team} size={46} />
                  </div>
                  <div style={{ marginTop: 13, display: "flex", justifyContent: "flex-end" }}><FormStrip form={homeSnapshot?.form} /></div>
                </div>
              </div>
              <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
                <div>
                  <div style={{ marginBottom: 8, color: C.sub, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.7 }}>
                    {fixture.away_team} recent scores
                  </div>
                  <RecentGames games={awaySnapshot?.recent_games} teamName={fixture.away_team} />
                </div>
                <div>
                  <div style={{ marginBottom: 8, color: C.sub, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.7 }}>
                    {fixture.home_team} recent scores
                  </div>
                  <RecentGames games={homeSnapshot?.recent_games} teamName={fixture.home_team} />
                </div>
              </div>
            </section>

            <section style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
              <PitcherCard label={fixture.away_team} pitcher={awayPitcher} overview={awayOverview} onOpen={() => openPlayerDrawer({ ...awayPitcher, teamId: context?.teams?.away?.id, teamName: fixture.away_team })} />
              <PitcherCard label={fixture.home_team} pitcher={homePitcher} overview={homeOverview} onOpen={() => openPlayerDrawer({ ...homePitcher, teamId: context?.teams?.home?.id, teamName: fixture.home_team })} />
            </section>

            <section style={{ ...PANEL, padding: 16 }}>
              <SectionTitle title="Betting Markets" sub="Moneyline, run line, and totals pulled directly from the baseball market summary." />
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                <div style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}` }}>
                  <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Moneyline</div>
                  {marketSummary.moneyline.away || marketSummary.moneyline.home ? (
                    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                        <span style={{ color: C.text }}>{fixture.away_team}</span>
                        <span style={{ color: "#caead8" }}>
                          {marketSummary.moneyline.away?.price != null ? marketSummary.moneyline.away.price.toFixed(2) : "—"}
                          {" • "}
                          {marketSummary.moneyline.away?.bookmaker || "—"}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                        <span style={{ color: C.text }}>{fixture.home_team}</span>
                        <span style={{ color: "#caead8" }}>
                          {marketSummary.moneyline.home?.price != null ? marketSummary.moneyline.home.price.toFixed(2) : "—"}
                          {" • "}
                          {marketSummary.moneyline.home?.bookmaker || "—"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, color: C.sub, fontSize: 13 }}>No moneyline available yet.</div>
                  )}
                </div>

                <div style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}` }}>
                  <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Run Line</div>
                  {marketSummary.runLine.away || marketSummary.runLine.home ? (
                    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                        <span style={{ color: C.text }}>{fixture.away_team} {marketSummary.runLine.away?.line ?? "—"}</span>
                        <span style={{ color: "#caead8" }}>
                          {marketSummary.runLine.away?.price != null ? marketSummary.runLine.away.price.toFixed(2) : "—"}
                          {" • "}
                          {marketSummary.runLine.away?.bookmaker || "—"}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                        <span style={{ color: C.text }}>{fixture.home_team} {marketSummary.runLine.home?.line ?? "—"}</span>
                        <span style={{ color: "#caead8" }}>
                          {marketSummary.runLine.home?.price != null ? marketSummary.runLine.home.price.toFixed(2) : "—"}
                          {" • "}
                          {marketSummary.runLine.home?.bookmaker || "—"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, color: C.sub, fontSize: 13 }}>No run line available yet.</div>
                  )}
                </div>

                <div style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}` }}>
                  <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Totals</div>
                  {marketSummary.total.over || marketSummary.total.under ? (
                    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                        <span style={{ color: C.text }}>Over {marketSummary.total.over?.line ?? marketSummary.total.under?.line ?? "—"}</span>
                        <span style={{ color: "#caead8" }}>
                          {marketSummary.total.over?.price != null ? marketSummary.total.over.price.toFixed(2) : "—"}
                          {" • "}
                          {marketSummary.total.over?.bookmaker || "—"}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                        <span style={{ color: C.text }}>Under {marketSummary.total.under?.line ?? marketSummary.total.over?.line ?? "—"}</span>
                        <span style={{ color: "#caead8" }}>
                          {marketSummary.total.under?.price != null ? marketSummary.total.under.price.toFixed(2) : "—"}
                          {" • "}
                          {marketSummary.total.under?.bookmaker || "—"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, color: C.sub, fontSize: 13 }}>No totals available yet.</div>
                  )}
                </div>
              </div>
              {activeModelRead ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: 16,
                    borderRadius: 14,
                    border: "1px solid rgba(49,212,139,0.28)",
                    background: "linear-gradient(135deg, rgba(22,185,107,0.12), rgba(4,15,10,0.72))",
                  }}
                >
                  {modelReads.length > 1 ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                      {modelReads.map((read) => {
                        const active = read.key === activeModelRead.key;
                        return (
                          <button
                            key={read.key}
                            type="button"
                            onClick={() => setSelectedModelRead(read.key)}
                            style={{
                              border: `1px solid ${active ? C.green : C.border}`,
                              borderRadius: 999,
                              padding: "7px 11px",
                              background: active ? "rgba(49,212,139,.16)" : "rgba(255,255,255,.03)",
                              color: active ? C.green : C.sub,
                              fontSize: 11,
                              fontWeight: 800,
                              cursor: "pointer",
                            }}
                          >
                            {read.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ color: C.green, fontSize: 11, fontWeight: 900, letterSpacing: 1, textTransform: "uppercase" }}>
                      Model Read · {activeModelRead.category}
                    </span>
                    <span style={{ color: C.text, fontWeight: 900 }}>{activeModelRead.label}</span>
                  </div>
                  <div style={{ marginTop: 9, color: C.text, fontSize: 17, fontWeight: 900 }}>Why this pick rates well</div>
                  <div style={{ marginTop: 7, color: "#c6d9ce", fontSize: 13, lineHeight: 1.6 }}>{activeModelRead.why}</div>
                  <div style={{ marginTop: 10, display: "flex", gap: 12, flexWrap: "wrap", color: C.sub, fontSize: 12 }}>
                    {activeModelRead.modelProb != null ? <span>Model {(activeModelRead.modelProb * 100).toFixed(1)}%</span> : null}
                    {activeModelRead.fairPrice != null ? <span>Fair {activeModelRead.fairPrice.toFixed(2)}</span> : null}
                    {activeModelRead.price != null ? <span>Market {activeModelRead.price.toFixed(2)}</span> : null}
                    {activeModelRead.edge != null ? <span style={{ color: C.green, fontWeight: 800 }}>Edge {formatEdgePercent(activeModelRead.edge)}</span> : null}
                  </div>
                  {activeModelRead.performance ? (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ marginBottom: 8, color: C.text, fontSize: 14, fontWeight: 900 }}>
                        Performance at {activeModelRead.performance.line ?? activeModelRead.line} runs
                      </div>
                      <div style={{ marginBottom: 10, color: C.sub, fontSize: 12, lineHeight: 1.5 }}>
                        How often each team&apos;s games finished {activeModelRead.label.toLowerCase().startsWith("under") ? "under" : "over"} this exact total. Pushes are excluded from the percentage.
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 10 }}>
                        <LinePerformance
                          teamName={fixture.away_team}
                          performance={activeModelRead.performance.away}
                          side={activeModelRead.label.toLowerCase().startsWith("under") ? "under" : "over"}
                        />
                        <LinePerformance
                          teamName={fixture.home_team}
                          performance={activeModelRead.performance.home}
                          side={activeModelRead.label.toLowerCase().startsWith("under") ? "under" : "over"}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
              <div style={{ ...PANEL, padding: 16 }}>
                <SectionTitle title="Top Pitcher Props" sub="Quick scan on the strongest pitcher-oriented markets currently loaded." />
                <div style={{ display: "grid", gap: 10 }}>
                  {topPitcherProps.length ? topPitcherProps.map((row) => (
                    <button key={row.id} type="button" onClick={() => openPlayerDrawer(row)} style={{ background: C.card, borderRadius: 12, padding: 12, border: `1px solid ${C.border}`, textAlign: "left", cursor: "pointer", color: "inherit" }}>
                      <div style={{ color: C.text, fontWeight: 700 }}>{row.playerName}</div>
                      <div style={{ marginTop: 4, color: C.sub, fontSize: 12 }}>
                        {row.bestData?.marketLabel || row.marketLabel} • {row.bestData?.sideLabel || formatBaseballPropSide(row.bestSide || row.overData?.marketKey || row.underData?.marketKey, row.line)}
                      </div>
                      <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
                        <span style={{ color: edgeTone(row.bestEdge), fontWeight: 800 }}>{formatEdgePercent(row.bestEdge)}</span>
                        <span style={{ color: C.sub }}>{row.bookmaker || "—"}</span>
                      </div>
                      {row.bestData?.plainSummary ? <div style={{ marginTop: 6, color: "#dbece2", fontSize: 12 }}>{row.bestData.plainSummary}</div> : null}
                    </button>
                  )) : <div style={{ color: C.sub, fontSize: 13 }}>No pitcher props loaded yet.</div>}
                </div>
              </div>

              <div style={{ ...PANEL, padding: 16 }}>
                <SectionTitle title="Top Batter Props" sub="Best player-v-pitcher markets loaded for hitters in this game." />
                <div style={{ display: "grid", gap: 10 }}>
                  {topBatterProps.length ? topBatterProps.map((row) => (
                    <button key={row.id} type="button" onClick={() => openPlayerDrawer(row)} style={{ background: C.card, borderRadius: 12, padding: 12, border: `1px solid ${C.border}`, textAlign: "left", cursor: "pointer", color: "inherit" }}>
                      <div style={{ color: C.text, fontWeight: 700 }}>{row.playerName}</div>
                      <div style={{ marginTop: 4, color: C.sub, fontSize: 12 }}>
                        {row.bestData?.marketLabel || row.marketLabel} • {row.bestData?.sideLabel || formatBaseballPropSide(row.bestSide || row.overData?.marketKey || row.underData?.marketKey, row.line)}
                      </div>
                      <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
                        <span style={{ color: edgeTone(row.bestEdge), fontWeight: 800 }}>{formatEdgePercent(row.bestEdge)}</span>
                        <span style={{ color: C.sub }}>{row.bookmaker || "—"}</span>
                      </div>
                      {row.bestData?.plainSummary ? <div style={{ marginTop: 6, color: "#dbece2", fontSize: 12 }}>{row.bestData.plainSummary}</div> : null}
                    </button>
                  )) : <div style={{ color: C.sub, fontSize: 13 }}>No batter props loaded yet.</div>}
                </div>
              </div>
            </section>

            <section style={{ ...PANEL, padding: 16 }}>
              <SectionTitle title="Pitcher vs Hitter H2H" sub="One-season lookback matchup tables for both probable-pitcher sides." />
              <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
                {[
                  ["Home Pitcher vs Away Hitters", h2hData?.home_pitcher_vs_away_hitters],
                  ["Away Pitcher vs Home Hitters", h2hData?.away_pitcher_vs_home_hitters],
                ].map(([title, block]) => (
                  <div key={title} style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <div>
                        <div style={{ color: C.text, fontWeight: 800 }}>{title}</div>
                        <div style={{ marginTop: 4, color: C.sub, fontSize: 12 }}>
                          Seasons: {toArray(block?.seasons).join(", ") || "Current + previous"}
                        </div>
                      </div>
                      {block?.pitcher ? (
                        <PlayerIdentityButton
                          player={block.pitcher}
                          headshotUrl={firstDefined(block.pitcher?.headshot_url, playerHeadshot(block.pitcher?.id))}
                          subtitle={block.pitcher?.pitch_hand?.description || "Pitcher"}
                          onClick={() => openPlayerDrawer({ ...block.pitcher, teamId: block.pitcher?.current_team?.id, teamName: block.pitcher?.current_team?.name })}
                        />
                      ) : null}
                    </div>
                    {toArray(block?.hitters).length ? (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr>
                              {["Hitter", "Side", "PA", "AB", "H", "HR", "BB", "SO", "AVG"].map((heading) => (
                                <th key={heading} style={{ textAlign: heading === "Hitter" ? "left" : "right", paddingBottom: 8, color: C.sub, borderBottom: `1px solid ${C.border}` }}>
                                  {heading}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {toArray(block.hitters).map((hitter) => (
                              <tr key={`${title}-${hitter.player_id}`} style={{ borderTop: `1px solid ${C.border}` }}>
                                <td style={{ padding: "10px 0", minWidth: 200 }}>
                                  <PlayerIdentityButton
                                    player={{ full_name: hitter.player_name, player_name: hitter.player_name }}
                                    headshotUrl={firstDefined(hitter.headshot_url, playerHeadshot(hitter.player_id))}
                                    subtitle="Open player card"
                                    onClick={() => openPlayerDrawer({ id: hitter.player_id, full_name: hitter.player_name, playerId: hitter.player_id, teamName: title.includes("Away Hitters") ? fixture.away_team : fixture.home_team })}
                                  />
                                </td>
                                <td style={{ padding: "10px 0", textAlign: "right", color: C.text }}>{firstDefined(hitter.bat_side?.description, hitter.bat_side?.code, "—")}</td>
                                <td style={{ padding: "10px 0", textAlign: "right", color: C.text }}>{firstDefined(hitter.stats?.pa, "—")}</td>
                                <td style={{ padding: "10px 0", textAlign: "right", color: C.text }}>{firstDefined(hitter.stats?.ab, "—")}</td>
                                <td style={{ padding: "10px 0", textAlign: "right", color: C.text }}>{firstDefined(hitter.stats?.hits, "—")}</td>
                                <td style={{ padding: "10px 0", textAlign: "right", color: C.text }}>{firstDefined(hitter.stats?.home_runs, "—")}</td>
                                <td style={{ padding: "10px 0", textAlign: "right", color: C.text }}>{firstDefined(hitter.stats?.walks, "—")}</td>
                                <td style={{ padding: "10px 0", textAlign: "right", color: C.text }}>{firstDefined(hitter.stats?.strikeouts, "—")}</td>
                                <td style={{ padding: "10px 0", textAlign: "right", color: C.text }}>{firstDefined(hitter.stats?.avg, "—")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ color: C.sub, fontSize: 13 }}>No hitter-v-pitcher history returned yet for this side.</div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {tab === "pitchers" && (
          <div style={{ marginTop: 18, display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            <PitcherCard label={fixture.away_team} pitcher={awayPitcher} overview={awayOverview} onOpen={() => openPlayerDrawer({ ...awayPitcher, teamId: context?.teams?.away?.id, teamName: fixture.away_team })} />
            <PitcherCard label={fixture.home_team} pitcher={homePitcher} overview={homeOverview} onOpen={() => openPlayerDrawer({ ...homePitcher, teamId: context?.teams?.home?.id, teamName: fixture.home_team })} />
          </div>
        )}

        {tab === "rosters" && (
          <div style={{ marginTop: 18, display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
            {[
              [fixture.away_team, awayRoster],
              [fixture.home_team, homeRoster],
            ].map(([teamName, roster]) => (
              <section key={teamName} style={{ ...PANEL, padding: 16 }}>
                <SectionTitle title={teamName} sub="Active roster / likely lineup block." />
                <div style={{ display: "grid", gap: 8 }}>
                  {roster.length ? roster.map((player) => {
                    const overview = overviews[String(player.id)] || null;
                    const positionLabel = playerPositionLabel(player, overview);
                    const batLabel = playerBatLabel(player, overview);
                    const throwLabel = playerThrowLabel(player, overview);
                    return (
                      <button key={`${teamName}-${player.id}`} type="button" onClick={() => openPlayerDrawer({ ...player, ...overview, playerId: player.id, teamName, teamId: teamName === fixture.home_team ? context?.teams?.home?.id : context?.teams?.away?.id })} style={{ background: C.card, borderRadius: 12, padding: 10, border: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", gap: 10, textAlign: "left", color: "inherit", cursor: "pointer" }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <div style={{ width: 38, height: 38, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,0.08)", flexShrink: 0 }}>
                            <img src={firstDefined(overview?.headshot_url, player.headshot_url, playerHeadshot(player.id))} alt="" width={38} height={38} style={{ objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          </div>
                          <div>
                            <div style={{ color: C.text, fontWeight: 700 }}>{player.full_name}</div>
                            <div style={{ marginTop: 4, color: C.sub, fontSize: 12 }}>
                              {positionLabel || "Player"}
                              {batLabel ? ` • Bats ${batLabel}` : ""}
                              {throwLabel ? ` • Throws ${throwLabel}` : ""}
                            </div>
                          </div>
                        </div>
                        <div style={{ color: "#caead8", fontSize: 12, fontWeight: 700 }}>
                          #{firstDefined(overview?.primary_number, player.primary_number, player.jersey_number, "—")}
                        </div>
                      </button>
                    );
                  }) : <div style={{ color: C.sub, fontSize: 13 }}>Roster data unavailable.</div>}
                </div>
              </section>
            ))}
          </div>
        )}

        {tab === "props" && (
          <div style={{ marginTop: 18, display: "grid", gap: 16 }}>
            <section style={{ ...PANEL, padding: 16 }}>
              <SectionTitle title="Player Props" sub="Grouped and merged client-side so over/under markets show as a single prop row where possible." />
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  value={propSearch}
                  onChange={(e) => setPropSearch(e.target.value)}
                  placeholder="Search player or market"
                  style={{
                    height: 38,
                    minWidth: 240,
                    borderRadius: 10,
                    border: `1px solid ${C.border}`,
                    background: "rgba(248,250,252,0.96)",
                    color: "#08130f",
                    padding: "0 12px",
                  }}
                />
                <Chip active={activePropFilter === "all"} onClick={() => setActivePropFilter("all")}>All Props</Chip>
                {PROP_GROUPS.map(([key, label]) => (
                  <Chip key={key} active={activePropFilter === key} onClick={() => setActivePropFilter(key)}>
                    {label}
                  </Chip>
                ))}
                <span style={{ color: C.sub, fontSize: 12 }}>
                  {propsRows.length} prop prices loaded
                  {propsMetaSummary.players != null ? ` • ${propsMetaSummary.players} players` : ""}
                  {propsMetaSummary.markets != null ? ` • ${propsMetaSummary.markets} prop categories` : ""}
                  {propsMetaSummary.bookmakers.length ? ` • Books: ${propsMetaSummary.bookmakers.slice(0, 4).join(", ")}` : ""}
                </span>
              </div>
            </section>

            {visiblePropGroups.length ? visiblePropGroups.map(([groupKey, rows]) => {
              const label = firstDefined(PROP_GROUPS.find(([key]) => key === groupKey)?.[1], groupKey);
              return (
                <section key={groupKey} style={{ ...PANEL, padding: 16 }}>
                  <SectionTitle title={label} sub={`${rows.length} market row${rows.length === 1 ? "" : "s"} available`} />
                  <div style={{ display: "grid", gap: 12 }}>
                    {rows.map((row) => (
                      <div key={row.id} style={{ background: row.bestEdge > 0.01 ? "rgba(49,212,139,0.05)" : C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() => openPlayerDrawer(row)}
                            style={{
                              display: "flex",
                              gap: 10,
                              alignItems: "center",
                              background: "transparent",
                              border: 0,
                              padding: 0,
                              color: "inherit",
                              cursor: "pointer",
                              textAlign: "left",
                            }}
                          >
                            <div style={{ width: 36, height: 36, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden", flexShrink: 0 }}>
                              {row.playerId ? (
                                <img
                                  src={playerHeadshot(row.playerId)}
                                  alt=""
                                  width={36}
                                  height={36}
                                  style={{ objectFit: "cover" }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : null}
                            </div>
                            <div>
                              <div style={{ color: C.text, fontWeight: 800 }}>{row.playerName}</div>
                              <div style={{ marginTop: 2, color: C.sub, fontSize: 12 }}>
                                {row.bestData?.marketLabel || row.marketLabel} • {row.bestData?.sideLabel || formatBaseballPropSide(row.bestSide || row.overData?.marketKey || row.underData?.marketKey, row.line)}
                              </div>
                            </div>
                          </button>
                          <div style={{ color: edgeTone(row.bestEdge), fontSize: 18, fontWeight: 900 }}>{formatEdgePercent(row.bestEdge)}</div>
                        </div>

                        {row.bestData?.plainSummary ? <div style={{ marginTop: 8, color: "#e4f3ea", fontSize: 13 }}>{row.bestData.plainSummary}</div> : null}
                        {row.bestData?.why ? <div style={{ marginTop: 6, color: C.sub, fontSize: 12, lineHeight: 1.5 }}>{row.bestData.why}</div> : null}
                        <FactorChips factors={row.bestData?.keyFactors} />

                        <div style={{ marginTop: 10, display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                          <div>
                            <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Over</div>
                            <div style={{ marginTop: 4, color: row.overPrice ? (row.bestSide === "over" && row.bestEdge > 0.01 ? "#86efac" : "#d6f5e3") : C.sub, fontWeight: 800 }}>
                              {row.overPrice ? row.overPrice.toFixed(2) : "—"}
                            </div>
                            <div style={{ marginTop: 2, color: C.sub, fontSize: 12 }}>{row.overData?.sideLabel || formatBaseballPropSide(row.overData?.marketKey || `${row.groupKey}_over`, row.line)}</div>
                          </div>
                          <div>
                            <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Under</div>
                            <div style={{ marginTop: 4, color: row.underPrice ? (row.bestSide === "under" && row.bestEdge > 0.01 ? "#86efac" : "#d6f5e3") : C.sub, fontWeight: 800 }}>
                              {row.underPrice ? row.underPrice.toFixed(2) : "—"}
                            </div>
                            <div style={{ marginTop: 2, color: C.sub, fontSize: 12 }}>{row.underData?.sideLabel || formatBaseballPropSide(row.underData?.marketKey || `${row.groupKey}_under`, row.line)}</div>
                          </div>
                          <div>
                            <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Model</div>
                            <div style={{ marginTop: 4, color: C.text, fontWeight: 700 }}>{formatModelPercent(firstDefined(row.bestData?.modelProb, row.overData?.modelProb, row.underData?.modelProb))}</div>
                          </div>
                          <div>
                            <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Fair</div>
                            <div style={{ marginTop: 4, color: C.text, fontWeight: 700 }}>{firstDefined(row.bestData?.fairPrice, row.overData?.fairPrice, row.underData?.fairPrice)?.toFixed?.(2) || "—"}</div>
                          </div>
                          <div>
                            <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Price</div>
                            <div style={{ marginTop: 4, color: C.text, fontWeight: 700 }}>{firstDefined(row.bestData?.price, row.overPrice, row.underPrice)?.toFixed?.(2) || "—"}</div>
                          </div>
                          <div>
                            <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Book</div>
                            <div style={{ marginTop: 4, color: C.text, fontWeight: 700 }}>{row.bookmaker || "—"}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            }) : (
              <section style={{ ...PANEL, padding: 16 }}>
                <div style={{ color: C.sub, fontSize: 13 }}>
                  {propsRows.length
                    ? "No player props matched the current filters."
                    : "No player prop rows are available from /api/player-odds for this fixture yet."}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {selectedPlayer ? (
        <div
          onClick={closePlayerDrawer}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.62)",
            zIndex: 2000,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 460,
              height: "100%",
              overflowY: "auto",
              background: "linear-gradient(180deg, rgba(9,18,15,0.99), rgba(5,12,10,0.99))",
              borderLeft: `1px solid ${C.border}`,
              boxShadow: "-16px 0 40px rgba(0,0,0,0.35)",
              padding: 18,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ color: C.green, fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase" }}>
                  Player Detail
                </div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 800 }}>
                  {firstDefined(selectedPlayer.row?.playerName, selectedPlayer.row?.full_name, "Player")}
                </div>
              </div>
              <button
                type="button"
                onClick={closePlayerDrawer}
                style={{
                  border: `1px solid ${C.border}`,
                  background: "rgba(255,255,255,0.05)",
                  color: C.text,
                  borderRadius: 10,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Close
              </button>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 14, alignItems: "center" }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 999,
                  overflow: "hidden",
                  background: "rgba(255,255,255,0.08)",
                  flexShrink: 0,
                }}
              >
                {selectedPlayer.row?.playerId ? (
                  <img
                    src={firstDefined(selectedOverview?.headshot_url, selectedPlayer.row?.headshot_url, playerHeadshot(selectedPlayer.row.playerId))}
                    alt=""
                    width={72}
                    height={72}
                    style={{ objectFit: "cover" }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : null}
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ color: C.sub, fontSize: 13 }}>{selectedTeam}</div>
                <div style={{ color: "#d7e7dd", fontSize: 13 }}>
                  {selectedPosition} • Bats {selectedBatting} • Throws {selectedPitching}
                </div>
                <div style={{ color: C.sub, fontSize: 12 }}>
                  {selectedPlayer.row?.playerId ? `MLB Player ID ${selectedPlayer.row.playerId}` : "No MLB player id loaded"}
                </div>
              </div>
            </div>

            {selectedPlayerLoading ? (
              <div style={{ marginTop: 16, color: C.sub, fontSize: 13 }}>Loading player overview…</div>
            ) : null}

            <div style={{ marginTop: 18, display: "grid", gap: 12 }}>
              <section style={{ ...PANEL, padding: 14 }}>
                <SectionTitle title="Season Snapshot" sub="Current and previous season data from the MLB player overview endpoint." />
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))" }}>
                  {[
                    ["ERA", selectedPitchingStats.era ?? "—", selectedBucket.previous?.era],
                    ["WHIP", selectedPitchingStats.whip ?? "—", selectedBucket.previous?.whip],
                    ["IP", selectedPitchingStats.innings ?? "—", firstDefined(selectedBucket.previous?.innings_pitched, selectedBucket.previous?.inningsPitched, selectedBucket.previous?.ip)],
                    ["SO", selectedPitchingStats.strikeouts ?? "—", firstDefined(selectedBucket.previous?.strike_outs, selectedBucket.previous?.strikeOuts, selectedBucket.previous?.strikeouts)],
                    ["AVG", selectedBattingStats.avg ?? "—", selectedBucket.previous?.avg],
                    ["OPS", selectedBattingStats.ops ?? "—", selectedBucket.previous?.ops],
                    ["H", selectedBattingStats.hits ?? "—", selectedBucket.previous?.hits],
                    ["HR", selectedBattingStats.homeRuns ?? "—", firstDefined(selectedBucket.previous?.home_runs, selectedBucket.previous?.homeRuns)],
                    ["RBI", selectedBattingStats.rbi ?? "—", selectedBucket.previous?.rbi],
                  ].map(([labelText, currentValue, previousValue]) => (
                    <StatComparisonBlock key={labelText} label={labelText} current={currentValue} previous={previousValue} />
                  ))}
                </div>
              </section>

              <section style={{ ...PANEL, padding: 14 }}>
                <SectionTitle title="Recent Performance" sub="Recent game log from the player overview payload when available." />
                {selectedRecentGames.length ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {selectedRecentGames.map((game, index) => (
                      <div key={`recent-${index}`} style={{ background: C.card, borderRadius: 12, padding: 10, border: `1px solid ${C.border}` }}>
                        <div style={{ color: C.text, fontWeight: 700 }}>
                          {game?.away_team && game?.home_team
                            ? `${game.away_team} at ${game.home_team}`
                            : firstDefined(game?.opponent, `Game ${index + 1}`)}
                        </div>
                        <div style={{ marginTop: 4, color: C.sub, fontSize: 12 }}>
                          {firstDefined(game?.game_date, game?.official_date, game?.date, "Date unavailable")}
                        </div>
                        <div style={{ marginTop: 5, color: "#dbece2", fontSize: 12 }}>
                          {Object.entries(game?.pitching || game?.batting || game?.stat_line || game?.stats || game?.stat || {})
                            .slice(0, 6)
                            .map(([key, value]) => `${key.replace(/([A-Z])/g, " $1").replace(/_/g, " ")}: ${value}`)
                            .join(" • ") || "Recent game details unavailable"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: C.sub, fontSize: 13 }}>
                    No recent game-log rows were returned for this player yet.
                  </div>
                )}
              </section>

              <section style={{ ...PANEL, padding: 14 }}>
                <SectionTitle title="Props In This Fixture" sub="All currently loaded props for this player in the active matchup." />
                {selectedPlayer.fixtureRows?.length ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {selectedPlayer.fixtureRows.map((row) => (
                      <div key={`player-prop-${row.id}`} style={{ background: row.bestEdge > 0.01 ? "rgba(49,212,139,0.06)" : C.card, borderRadius: 12, padding: 10, border: `1px solid ${C.border}` }}>
                        <div style={{ color: C.text, fontWeight: 700 }}>
                          {row.bestData?.marketLabel || row.marketLabel} {row.bestData?.sideLabel ? `— ${row.bestData.sideLabel}` : row.bestSide ? `— ${formatBaseballPropSide(row.bestSide === "under" ? row.underData?.marketKey : row.overData?.marketKey, row.line)}` : row.line != null ? `• ${row.line}` : ""}
                        </div>
                        <div style={{ marginTop: 4, color: C.sub, fontSize: 12 }}>
                          Over {row.overPrice != null ? row.overPrice.toFixed(2) : "—"} • Under {row.underPrice != null ? row.underPrice.toFixed(2) : "—"} • {row.bookmaker || "—"}
                        </div>
                        <div style={{ marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap", fontSize: 12 }}>
                          <span style={{ color: C.sub }}>Model {formatModelPercent(firstDefined(row.bestData?.modelProb, row.overData?.modelProb, row.underData?.modelProb))}</span>
                          <span style={{ color: C.sub }}>
                            Fair {firstDefined(row.bestData?.fairPrice, row.overData?.fairPrice, row.underData?.fairPrice)?.toFixed?.(2) || "—"}
                          </span>
                          <span style={{ color: edgeTone(row.bestEdge), fontWeight: 800 }}>{formatEdgePercent(row.bestEdge)}</span>
                        </div>
                        {row.bestData?.plainSummary ? <div style={{ marginTop: 6, color: "#dbece2", fontSize: 12 }}>{row.bestData.plainSummary}</div> : null}
                        {row.bestData?.why ? <div style={{ marginTop: 6, color: C.sub, fontSize: 12, lineHeight: 1.5 }}>{row.bestData.why}</div> : null}
                        <FactorChips factors={row.bestData?.keyFactors} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: C.sub, fontSize: 13 }}>No props are currently loaded for this player in this fixture.</div>
                )}
              </section>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
